import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  AlunoCreateInput,
  type AlunoCreateInputType,
  type AlunoOutputType,
  type MateriaOutputType,
} from '@shared/dto';

import { paraInputDate } from '../../../lib/format-date';
import { useApiMutation, useApiQuery } from '../../../hooks/use-api';
import { alunosApi } from '../../../api/alunos.api';
import { useAuth } from '../../../hooks/use-auth';
import { Accordion } from '../../ui/accordion';
import { Button } from '../../ui/button';
import { DialogFooter } from '../../ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../ui/form';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Switch } from '../../ui/switch';
import { Textarea } from '../../ui/textarea';
import { SITUACAO_ALUNO_LABEL } from './enum-labels';
import { MatriculaExistenteCard } from './matricula-existing-card';
import { NovaMatriculaForm } from './nova-matricula-form';

/**
 * O form trabalha com strings "AAAA-MM-DD" (`z.coerce.date()` gera input `unknown` ≠ output `Date`); a conversão é no
 * `onSubmit`. `useForm` usa os 3 genéricos porque `situacao`/`zonaVermelha`/`connect` têm `.default(...)`.
 */
const AlunoFormSchema = AlunoCreateInput.extend({
  dataNascimento: z.string().optional(),
  dataMatricula: z.string().min(1, 'obrigatório'),
});
type AlunoFormInput = z.input<typeof AlunoFormSchema>;
type AlunoFormOutput = z.output<typeof AlunoFormSchema>;

export function AlunoForm({
  aluno,
  materias,
  onSalvo,
  onPersistido,
  onPainelAtualizado,
}: {
  aluno?: AlunoOutputType;
  materias: MateriaOutputType[];
  onSalvo: () => void;
  /** Atualiza as listagens depois do primeiro POST sem fechar ou remontar o modal. */
  onPersistido?: () => void;
  /** Mutação em matrícula/horário já existente: não fecha o dialog, só avisa que o snapshot do painel ficou desatualizado. */
  onPainelAtualizado?: () => void;
}) {
  const { isAdmin, podeGerenciarMatriculas } = useAuth();
  const [alunoPersistido, setAlunoPersistido] = useState(aluno);
  const [adicionandoMatricula, setAdicionandoMatricula] = useState(false);

  const { mutate: criarAluno, loading: criando } = useApiMutation(alunosApi.criar);
  const { mutate: atualizarAluno, loading: atualizando } = useApiMutation(alunosApi.atualizar);

  const { data: matriculasExistentes, refetch: refetchMatriculas } = useApiQuery(
    alunosApi.listarMatriculas,
    alunoPersistido?.id ?? '',
    { enabled: Boolean(alunoPersistido) },
  );

  const form = useForm<AlunoFormInput, unknown, AlunoFormOutput>({
    resolver: zodResolver(AlunoFormSchema),
    defaultValues: aluno
      ? {
          nome: aluno.nome,
          responsavel: aluno.responsavel ?? undefined,
          telefone: aluno.telefone ?? undefined,
          whatsapp: aluno.whatsapp ?? undefined,
          email: aluno.email ?? undefined,
          dataNascimento: paraInputDate(aluno.dataNascimento) || undefined,
          observacoes: aluno.observacoes ?? undefined,
          dataMatricula: paraInputDate(aluno.dataMatricula),
          situacao: aluno.situacao,
          zonaVermelha: aluno.zonaVermelha,
          connect: aluno.connect,
        }
      : {
          nome: '',
          dataMatricula: paraInputDate(new Date()),
          situacao: 'ATIVO',
          zonaVermelha: false,
          connect: false,
        },
  });

  async function onSubmit(dados: AlunoFormOutput) {
    // Viajam como "AAAA-MM-DD" e `z.coerce.date()` do backend converte; o cast é só pro tipo do contrato (`Date`).
    const corpo = {
      ...dados,
      dataNascimento: dados.dataNascimento || undefined,
    } as unknown as AlunoCreateInputType;

    if (alunoPersistido) {
      await atualizarAluno({ id: alunoPersistido.id, dados: corpo });
      onSalvo();
      return;
    }

    const novoAluno = await criarAluno(corpo);
    setAlunoPersistido(novoAluno);
    setAdicionandoMatricula(true);
    onPersistido?.();
  }

  const salvando = criando || atualizando;
  const rotuloBotaoSalvar = salvando
    ? 'Salvando...'
    : !alunoPersistido
      ? 'Criar aluno'
      : aluno
        ? 'Salvar'
        : 'Concluir';

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-sm font-medium">Dados pessoais</h3>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="nome"
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormLabel>Nome completo</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="responsavel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Responsável</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="telefone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="whatsapp"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>WhatsApp</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="dataNascimento"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data de nascimento</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="observacoes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Observações gerais</FormLabel>
                <FormControl>
                  <Textarea {...field} value={field.value ?? ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="dataMatricula"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data da matrícula</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="situacao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Situação do aluno</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="ATIVO">{SITUACAO_ALUNO_LABEL.ATIVO}</SelectItem>
                      <SelectItem value="TRANCADO">{SITUACAO_ALUNO_LABEL.TRANCADO}</SelectItem>
                      <SelectItem value="DESISTENTE">{SITUACAO_ALUNO_LABEL.DESISTENTE}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-medium">Categorias</h3>
          <div className="flex flex-wrap gap-6">
            <FormField
              control={form.control}
              name="zonaVermelha"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <Label className="!mt-0">Zona Vermelha</Label>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="connect"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <Label className="!mt-0">Connect</Label>
                </FormItem>
              )}
            />
          </div>
        </div>

        {alunoPersistido && podeGerenciarMatriculas ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Matrículas</h3>
              {!adicionandoMatricula ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAdicionandoMatricula(true)}
                >
                  <Plus className="size-4" />
                  Nova matrícula
                </Button>
              ) : null}
            </div>

            <div className="space-y-3">
              {matriculasExistentes && matriculasExistentes.length > 0 ? (
                <Accordion type="multiple" className="rounded-lg border">
                  {matriculasExistentes.map((matricula) => (
                    <MatriculaExistenteCard
                      key={matricula.id}
                      matricula={matricula}
                      materias={materias}
                      onAtualizada={() => {
                        void refetchMatriculas();
                        onPainelAtualizado?.();
                      }}
                    />
                  ))}
                </Accordion>
              ) : null}
              {adicionandoMatricula ? (
                <NovaMatriculaForm
                  alunoId={alunoPersistido.id}
                  materias={materias}
                  onCriada={() => {
                    setAdicionandoMatricula(false);
                    void refetchMatriculas();
                    onPainelAtualizado?.();
                  }}
                  onCancelar={() => setAdicionandoMatricula(false)}
                />
              ) : null}
            </div>
          </div>
        ) : null}

        <DialogFooter>
          {isAdmin ? (
            <Button type="submit" disabled={salvando}>
              {rotuloBotaoSalvar}
            </Button>
          ) : (
            <Popover>
              <PopoverTrigger asChild>
                {/* `span`, não o `Button`: um botão `disabled` nunca dispara o clique que abre o popover. */}
                <span tabIndex={0}>
                  <Button type="button" disabled>
                    {rotuloBotaoSalvar}
                  </Button>
                </span>
              </PopoverTrigger>
              <PopoverContent className="w-auto text-sm">
                Apenas administradores podem editar dados do aluno.
              </PopoverContent>
            </Popover>
          )}
        </DialogFooter>
      </form>
    </Form>
  );
}
